import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { apiGet } from "../../../lib/api.js";

/**
 * B-GTCC-A17 — the read hooks behind the Device-Approval view
 * (register row 28, ticket A-GTCC-13).
 *
 * Row 28 is "Device Approval (freigeben, ablehnen)". This module owns the
 * reading half; the view uses the existing device-approval write endpoints.
 *
 * The endpoints used, all measured:
 *
 *   - `GET /v1/admin/devices/pending` — the open queue,
 *     `services/api/src/modules/admin/device-approval.routes.ts:69`,
 *     gated by `requirePlatformOwner("devices")` (:80). Returns
 *     `PendingDeviceListRow[]` (`device-approval.service.ts:54`) wrapped
 *     in `{ items }`; `limit` is clamped to 1..200 by the route schema.
 *   - `GET /v1/admin/audit` — the decided half,
 *     `services/api/src/modules/admin/audit.routes.ts:47`, permission
 *     `tenant.read`. There is no "list decided device approvals"
 *     endpoint: `approve()` and `reject()` write one audit row each with
 *     action `device.approve` / `device.reject`
 *     (`device-approval.service.ts:383`), and that audit row is the only
 *     durable record of the decision that a reading surface can query.
 *     Both actions are in the closed `AUDIT_ACTIONS` enum
 *     (`modules/audit/dto.ts:51`), so the `action` filter is accepted
 *     rather than answered with `invalid_filter`.
 *
 * Two scope facts that the view has to be honest about:
 *
 *   1. The two halves have different reach. The pending queue is a
 *      cross-tenant platform read (SECURITY DEFINER,
 *      `device-approval.service.ts:199`); the audit list is scoped to
 *      the calling tenant by `withTenantConnection`
 *      (`audit/list.service.ts`). The decided lists therefore show the
 *      decisions recorded for the current tenant, not for the whole
 *      platform, and the view says so instead of implying completeness.
 *
 *   2. The IMEI of a decided device lives in the audit row's state
 *      snapshots, not in a column: `beforeState.imei` and
 *      `afterState.imei` are both written by the service
 *      (`device-approval.service.ts:386` and following). Reading it back
 *      needs a narrowing helper, because the DTO types both snapshots as
 *      `Record<string, unknown> | null`.
 *
 * The view reuses `POST /v1/admin/devices/:id/approve` (`routes.ts:103`) and
 * `POST /v1/admin/devices/:id/reject` (`routes.ts:127`) from the established
 * device-approval page, then invalidates these read queries.
 */

/** Mirrors `PendingDeviceListRow`, `admin/device-approval.service.ts:54`. */
export interface GtccPendingDevice {
  readonly id: string;
  readonly tenantId: string | null;
  readonly tenantDisplayName: string | null;
  readonly tenantSlug: string | null;
  readonly registeredBySub: string | null;
  readonly registeredByEmail: string | null;
  readonly imei: string;
  readonly deviceType: string;
  readonly createdAt: string;
}

export interface GtccPendingDevicesResponse {
  readonly items: readonly GtccPendingDevice[];
  /**
   * PAKET 003240 — the route reports whether the queue continues past the
   * requested page (`device-approval.routes.ts`). Optional here because a
   * cached response from before that change carries no such field; the view
   * treats the absence as "nothing known", never as "the list is complete".
   */
  readonly truncated?: boolean;
}

/** Mirrors `AuditEntryDto`, `modules/audit/list.service.ts:53`. */
export interface GtccApprovalAuditEntry {
  readonly id: string;
  readonly tenantId: string;
  readonly actorSub: string;
  readonly actorEmail: string;
  readonly action: string;
  readonly targetType: string;
  readonly targetId: string;
  readonly beforeState: Record<string, unknown> | null;
  readonly afterState: Record<string, unknown> | null;
  readonly createdAt: string;
}

export interface GtccApprovalAuditResponse {
  readonly items: readonly GtccApprovalAuditEntry[];
  readonly nextCursor: string | null;
}

export const GTCC_APPROVAL_DECISIONS = ["device.approve", "device.reject"] as const;
export type GtccApprovalDecision = (typeof GTCC_APPROVAL_DECISIONS)[number];

/**
 * The queue and the two decision lists show their newest page rather
 * than paging through it. 50 is what the pending route itself defaults
 * to (`device-approval.routes.ts:75`); the audit route caps `limit` at
 * 200 and `nextCursor` tells the view to declare the list truncated
 * instead of pretending it is complete.
 */
export const GTCC_APPROVAL_PAGE_SIZE = 50;

export function useGtccPendingDevices(): UseQueryResult<GtccPendingDevicesResponse, Error> {
  return useQuery<GtccPendingDevicesResponse, Error>({
    queryKey: ["gtcc", "device-approval", "pending"],
    queryFn: ({ signal }) =>
      apiGet<GtccPendingDevicesResponse>(
        "/v1/admin/devices/pending",
        { limit: GTCC_APPROVAL_PAGE_SIZE },
        signal,
      ),
    staleTime: 30_000,
  });
}

export function useGtccApprovalDecisions(
  action: GtccApprovalDecision,
): UseQueryResult<GtccApprovalAuditResponse, Error> {
  return useQuery<GtccApprovalAuditResponse, Error>({
    queryKey: ["gtcc", "device-approval", "decisions", action],
    queryFn: ({ signal }) =>
      apiGet<GtccApprovalAuditResponse>(
        "/v1/admin/audit",
        { action, target_type: "device", limit: GTCC_APPROVAL_PAGE_SIZE },
        signal,
      ),
    staleTime: 30_000,
  });
}

/**
 * Reads a string field out of an audit-state snapshot. Both snapshots
 * are `Record<string, unknown> | null` in the DTO, so every access has
 * to narrow; returning `null` for a missing or non-string value keeps
 * historical rows rendering as "—" instead of crashing the table.
 */
export function auditStateString(
  state: Record<string, unknown> | null,
  key: string,
): string | null {
  if (!state) return null;
  const value = state[key];
  return typeof value === "string" ? value : null;
}
